import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import styled from "styled-components";

/* brand.css has no alpha tokens, so translucent values are mixed from the solid
   brand colors rather than hardcoded as rgba(). Two layers: a tight ambient
   shadow to seat the panel, and a wide soft one for the diffuse lift. */
const panelShadow = [
  "0 4px 12px color-mix(in srgb, var(--brand-black) 6%, transparent)",
  "0 16px 40px color-mix(in srgb, var(--brand-black) 14%, transparent)",
].join(", ");
const focusRing =
  "color-mix(in srgb, var(--brand-purple-700) 15%, transparent)";

/* Much lighter than the panel — the pill sits on the page, it doesn't float
   above it. */
const triggerShadow = [
  "0 1px 2px color-mix(in srgb, var(--brand-black) 5%, transparent)",
  "0 2px 6px color-mix(in srgb, var(--brand-black) 6%, transparent)",
].join(", ");
const triggerShadowHover = [
  "0 1px 2px color-mix(in srgb, var(--brand-black) 6%, transparent)",
  "0 4px 10px color-mix(in srgb, var(--brand-black) 10%, transparent)",
].join(", ");

export const TriggerStyled = styled.button<{ $open: boolean }>`
  gap: 10px;
  padding: 10px 16px;
  max-width: 280px;

  display: flex;
  align-items: center;

  border-radius: 12px;
  cursor: pointer;

  font-family: inherit;
  font-size: 15px;
  font-weight: var(--font-weight-medium);
  line-height: 1.25;

  box-shadow: ${triggerShadow};

  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, color 0.15s ease;

  ${({ $open }) =>
    $open
      ? `
    border: 1px solid var(--brand-purple-500);
    background-color: var(--brand-blue-25);
    color: var(--brand-purple-700);
  `
      : `
    border: 1px solid var(--brand-gray-200);
    background-color: var(--brand-gray-50);
    color: var(--brand-black);
  `}

  &:hover {
    background-color: var(--brand-gray-100);
    box-shadow: ${triggerShadowHover};
  }

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: 2px;
  }
`;

export const TriggerLabelStyled = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const ChevronStyled = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  /* The shared ChevronDown ships at 14px; the pill reads better with a
     slightly larger, lighter caret. Inherits the purple when open. */
  color: ${({ $open }) => ($open ? "currentColor" : "var(--brand-gray-500)")};

  svg {
    width: 16px;
    height: 16px;
  }

  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  transition: transform 0.2s ease;
`;

/* PrimeReact draws a caret via ::before/::after and pads the content wrapper —
   both reset so the panel reads as a plain rounded card. */
export const PanelStyled = styled(OverlayPanel)`
  &.p-overlaypanel {
    width: 320px;
    margin-top: 8px;

    border: 1px solid var(--brand-gray-200);
    border-radius: 16px;
    box-shadow: ${panelShadow};

    &::before,
    &::after {
      display: none;
    }

    .p-overlaypanel-content {
      padding: 16px 12px 12px;
    }
  }
`;

export const SectionLabelStyled = styled.p`
  margin: 0 0 10px;
  padding-left: 8px;

  font-size: 11px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.08em;
  text-transform: uppercase;

  color: var(--brand-gray-600);
`;

export const SearchInputStyled = styled(InputText)`
  width: 100%;
  padding-left: 36px;

  font-size: 14px;
  border-radius: 10px;
  border-color: var(--brand-gray-200);

  &:enabled:focus {
    box-shadow: 0 0 0 3px ${focusRing};
    border-color: var(--brand-purple-600);
  }
`;

export const SearchIconStyled = styled.i`
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);

  font-size: 14px;
  color: var(--brand-gray-500);
`;

export const AccountListStyled = styled.div`
  max-height: 288px;
  overflow-y: auto;

  display: flex;
  flex-direction: column;
`;

export const AccountRowStyled = styled.div<{ $active: boolean }>`
  gap: 8px;
  padding-right: 8px;

  display: flex;
  align-items: center;

  border-radius: 10px;

  background-color: ${({ $active }) =>
    $active ? "var(--brand-blue-25)" : "transparent"};

  &:hover {
    background-color: var(--brand-gray-50);
  }
`;

export const AccountNameButtonStyled = styled.button<{ $active: boolean }>`
  gap: 8px;
  padding: 11px 8px;
  flex: 1;
  min-width: 0;

  display: flex;
  align-items: center;

  border: none;
  background: transparent;
  cursor: pointer;

  font-family: inherit;
  font-size: 15px;
  text-align: left;

  color: ${({ $active }) =>
    $active ? "var(--brand-purple-700)" : "var(--brand-black)"};
  font-weight: ${({ $active }) =>
    $active ? "var(--font-weight-semibold)" : "400"};

  &:focus-visible {
    outline: 2px solid var(--brand-purple-600);
    outline-offset: -2px;
    border-radius: 10px;
  }
`;

/* The ellipsis clip belongs to the name alone. As a bare `span {}` rule on the
   button it also matched the ping badge's spans and cut the halo off at the
   badge's own 16px box, so the pulse was invisible. */
export const AccountLabelStyled = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const EmptyStateStyled = styled.p`
  margin: 0;
  padding: 16px 8px;

  font-size: 14px;
  color: var(--brand-gray-600);
  text-align: center;
`;

export const FooterStyled = styled.div`
  margin-top: 8px;
  padding-top: 12px;

  display: flex;
  justify-content: flex-end;

  border-top: 1px solid var(--brand-gray-200);
`;

/* Matches the smaller switch the old inline dropdown used for this control. */
export const DefaultSwitchStyled = styled(InputSwitch)`
  width: 32px;
  height: 18px;
  flex-shrink: 0;

  .p-inputswitch-slider {
    border-radius: 12px;
  }

  .p-inputswitch-slider:before {
    width: 14px;
    height: 14px;
    top: 11px;
    left: 2px;
  }

  &.p-inputswitch-checked .p-inputswitch-slider:before {
    transform: translateX(12px);
  }
`;
